import { ExprCall } from '@mode-switch/express';
import { Attr, Names, Values } from '@mode-switch/model';
import { EncField, Encoding, MarkChannel, MarksSummary } from '@mode-vegas/vegas';
import { EncodingHelpers, FieldFormatHelpers, RenderDataModel, TOTALS_TOKEN } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { getTextFormatOptionsByShortcutType } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import * as ChartData from './muze-chart-data';
import * as ChartLayers from './muze-chart-layers';
import { muze } from './muze';

const REF_ID = '__id__';

interface NullValue {
  _value: string;
}

interface TooltipRow {
  name: string;
  value: number | string | NullValue | undefined;
  isAllToken: boolean;
  role: Attr.Role;
  format?: ChartData.MuzeDataFormatFn;
}

type CompareTableEntry = {
  primary: string;
  highlight: boolean;
  responseValue: number | null | undefined;
  color: string | undefined;
  hideResponse: boolean;
  isSummaryRow: boolean;
};

function isTipEqual(first: TooltipRow, second: TooltipRow) {
  if (first.name !== second.name) {
    return false;
  }
  if (first.value?.toString() !== second.value?.toString()) {
    return false;
  }
  if ((!first.format && second.format) || (first.format && !second.format)) {
    return false;
  }
  // Can't directly compare the format functions since they will always be different objects even if they are the same format
  // so indirectly match the formatted value.
  if (
    first.format?.call(null, { rawValue: first.value }, { context: {} })?.toString() !==
    second.format?.call(null, { rawValue: second.value }, { context: {} })?.toString()
  ) {
    return false;
  }
  return true;
}

export function makeTooltip(model: RenderDataModel, encoding: Encoding, fieldFormats: ChartTypes.FieldFormatMap) {
  const html = muze.Operators.html;
  let fieldMap: Map<string, CompiledField> = new Map();
  return {
    formatter: (dataInfo: ChartData.MuzeTooltipDataInfo, contextInfo: ChartData.MuzeContextInfo) => {
      const rowData = makeTooltipHTMLString(model, encoding, fieldFormats, fieldMap, dataInfo, contextInfo);
      return rowData != null ? html` <div class="muze-tooltip-content">${rowData}</div> ` : '';
    },
  };
}

export function makeTooltipHTMLString(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  fieldMap: Map<string, CompiledField>,
  dataInfo: ChartData.MuzeTooltipDataInfo,
  contextInfo: ChartData.MuzeContextInfo
) {
  const dataStore = dataInfo.dataModel;
  let { data, schema } = dataStore.getData({
    excludeFields: [REF_ID],
  });

  const layer = contextInfo.context?.targetLayer;
  if (layer == null) {
    return null;
  }

  /**
   * @description
   * The tip generation is different with respect to the setting for 'highlightExactPoint'.
   * When 'highlightExactPoint' is true:
   *   - We get a single row of data when the hover is directly over a data element only.
   * When 'highlightExactPoint' is false,
   *   - We get 1 or more rows of data which includes all data elements within the same
   *     categorical 'slice' of data. The 'slice' being the dimension directly on the axis.
   *   - If the user is directly over a data element, the targetIds/values exactly
   *     represent the data element which is under the hover. These are provided in the
   *     'target' of the payload in the context object.
   *   - If the user is over the axis band, but not directly over any data element, then
   *     the targetIds/values are for the categorical axis value only. These are provided
   *     in the 'dimensions' of the criteria in the payload of the context object.
   *
   * For the cases above where the user is directly over a data element, we can create a
   * tooltip specific to that point (ie targeted) including all of the dimension and measure
   * information for that one data element.
   *
   * For the case where 'highlightExactPoint' is false and the user is not directly over
   * a data element but in the same band as some data elements, then we need to create
   * a tooltip which only contains the axis and facet information. This is a less specific
   * (ie untargeted) tooltip because we can't include response or channel information
   * without being over a specific data element.
   */
  let targetIds: string[] = [];
  let targetValues: string | number[] = [];

  let tooltipRows: Array<TooltipRow> = [];
  if ((contextInfo.context?.payload?.target ?? []).length > 1) {
    // This is the case of being directly over a data element
    targetIds = contextInfo.context.payload.target[0];
    targetValues = contextInfo.context.payload.target[1];
    tooltipRows = makeTargettedTipRows(
      model,
      encoding,
      fieldFormats,
      fieldMap,
      dataStore,
      contextInfo,
      targetIds,
      targetValues
    );
  } else if ((contextInfo.context?.payload?.criteria?.dimensions ?? []).length > 1) {
    // In this case, the user is near point but not directly over any element
    targetIds = contextInfo.context.payload.criteria.dimensions[0];
    targetValues = contextInfo.context.payload.criteria.dimensions[1];
    tooltipRows = makeUntargettedTipRows(model, encoding, fieldFormats, fieldMap, targetIds, targetValues, layer);
  } else {
    return null;
  }

  // Remove any duplicate tip entries
  let uniqueTooltipRows = tooltipRows.reduce((uniqueTips, tip) => {
    if (!uniqueTips.some((obj) => isTipEqual(obj, tip))) {
      uniqueTips.push(tip);
    }
    return uniqueTips;
  }, <TooltipRow[]>[]);

  // Sort dimensions before measures, then alphabetical
  uniqueTooltipRows.sort((a, b) => {
    if (a.role === b.role) {
      return a.name.localeCompare(b.name);
    }
    return a.role === Attr.Role.Dimension ? -1 : 1;
  });

  // Generate a row for each unique tooltip entry
  let rowData = uniqueTooltipRows
    .map((row) => {
      let formattedValue = row.format && !row.isAllToken ? row.format({ rawValue: row.value }, contextInfo) : row.value;
      if (!row.isAllToken && typeof formattedValue === 'string') {
        formattedValue = escapeXml(formattedValue);
      }
      return `
      <div class="muze-tooltip-row">
        <span class="muze-tooltip-content muze-tooltip-key">${escapeXml(row.name)}</span>
        <span class="muze-tooltip-content muze-tooltip-value">${formattedValue}</span>
      </div>
      `;
    })
    .join('');

  rowData = `<div class="muze-tooltip-row-content">${rowData}</div>`;
  // If we received more than one row of data, we may need to show a table comparing the individual
  // color or detail data.
  if (data.length > 1) {
    const muzeEncoding = layer.config().encoding;
    const colorField: string = muzeEncoding?.color?.field;
    const detailField: string[] = muzeEncoding?.detail?.field ?? [];

    const isDimension = (field: string) => {
      const schemaItem = schema.find((item: any) => item.name === field);
      return schemaItem != null && schemaItem.type === 'dimension';
    };

    const targetChannel = [colorField, ...detailField].find((fieldId) => isDimension(fieldId));
    const allDimensions = targetIds.reduce((all, fieldId) => {
      if (fieldId !== ChartData.SID_FIELD && isDimension(fieldId)) {
        all.push(fieldId);
      }
      return all;
    }, [] as Array<string>);
    // Make sure the target channel is included in the dimensions
    if (targetChannel && !allDimensions.some((dim) => dim === targetChannel)) {
      allDimensions.push(targetChannel);
    }

    const { primaryField, secondaryField } = getPrimarySecondaryFields(model, encoding, layer);

    // There is a particular situation where the encoding is the opposite of what is expected.
    // This occurs when there is a continuous date and a non-aggregated,continuous measure.
    // We would expect the measure to be the primary axis and the date to be secondary. Depending
    // on the order in which you build the chart, it can be reversed.
    //
    // Example:
    //   1. Add a date to Rows
    //   2. Add a measure to Rows
    //   3. Set the measure to No aggregation
    //   4. Move the measure to Columns
    // This creates a 'y' encoding where the date is the series. If we use the encoding as is, the
    // values shown in the tooltip would be formatted dates rather than the measure.
    //
    // Check for this situation and if detected, then swap the primary and secondary.
    const primaryModeField = primaryField ? model.compileField(primaryField) : undefined;
    const secondaryModeField = secondaryField ? model.compileField(secondaryField) : undefined;

    const responseField =
      primaryModeField?.hasDateFunc && !secondaryModeField?.hasDateFunc && secondaryModeField?.isContinuous
        ? secondaryField
        : primaryField;

    if (targetChannel != null && responseField?.type === Attr.Type.Continuous) {
      // The colorProvider is either a function which takes a category value and returns the color
      // or, a single color value (string).
      const colorProvider = colorField != null ? layer.axes().color : muzeEncoding?.color?.value();

      const includePercentsAndTotal = includePercentAndTotal(model, encoding, layer.config().mark, responseField);

      const tableHTML = makeCompareTable(
        model,
        encoding,
        fieldFormats,
        targetChannel,
        allDimensions,
        dataStore,
        targetIds,
        targetValues,
        responseField,
        colorProvider,
        includePercentsAndTotal
      );
      rowData += tableHTML;
    }
  }
  return rowData;
}

/**
 * Builds an Array of TooltipRow when the hover is directly targetting a data element.
 */
function makeTargettedTipRows(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  fieldMap: Map<string, CompiledField>,
  dataStore: any,
  contextInfo: ChartData.MuzeContextInfo,
  targetIds: string[],
  targetValues: string | number[]
) {
  let { data, schema } = dataStore.getData({
    excludeFields: [REF_ID],
  });
  const layer = contextInfo.context?.targetLayer;
  const { primaryField, secondaryField } = getPrimarySecondaryFields(model, encoding, layer);
  // Currently we don't care what type of totals this is. Muze will return
  // a string (ex: 'rowGrandTotals') if this is totals or null if not.
  const isTotals = layer.metaInf().totals != null;

  // Strip any items out of the schema which are part of a different series. Otherwise, those items will
  // show up in the tooltip with a null value.
  schema = removeNonTargetedSeriesSchemaItems(encoding, schema, contextInfo);

  const measureNamesSchemaEntry = schema.find((field: any) => {
    return field.name === Names.Key || EncodingHelpers.getEncFieldById(encoding, field.name)?.formula === Names.Key;
  });
  const measureNamesIndex = measureNamesSchemaEntry ? dataStore.getFieldIndex(measureNamesSchemaEntry.name) : -1;

  const tooltipRows: Array<TooltipRow> = [];

  data
    .filter((dataArray: any) => {
      // for stacked charts we need to loop over and find the hovered data row
      return targetIds.every((id, i) => {
        return id === REF_ID || asString(dataArray[dataStore.getFieldIndex(id)]) === asString(targetValues[i]);
      });
    })
    .forEach((dataArray: any) => {
      if (secondaryField && !isMeasureNames(secondaryField)) {
        tooltipRows.push({
          name: getFieldDisplayName(model, secondaryField, fieldFormats, fieldMap),
          role: secondaryField.role,
          ...getValueFromData(dataArray, dataStore, encoding, secondaryField.alias, isTotals),
          format: ChartData.buildMuzeTextFormatter(model, [secondaryField], fieldFormats),
        });
      }
      if (primaryField && !isMeasureNames(primaryField)) {
        let toolTipName = getFieldDisplayName(model, primaryField, fieldFormats, fieldMap);
        let formatterField = primaryField;
        if (isMeasureValues(primaryField)) {
          const measureNamesValue = dataArray[measureNamesIndex];

          if (measureNamesValue) {
            const valueField = getMeasureValueModeField(model, encoding, measureNamesValue, fieldMap);
            const fieldFormat = getFieldFormat(model, valueField, fieldFormats);
            toolTipName =
              (valueField &&
                FieldFormatHelpers.getBaseFormatDisplayName(
                  FieldFormatHelpers.getFormatMapKeyForModeField(valueField),
                  fieldFormats
                )) ||
              measureNamesValue;
            if (fieldFormat != null && fieldFormat.shortcutType !== ChartTypes.FormatShortcutType.Automatic) {
              formatterField = valueField ?? formatterField;
            }
          }
        }
        tooltipRows.push({
          name: toolTipName,
          role: primaryField.role,
          ...getValueFromData(dataArray, dataStore, encoding, primaryField.alias, isTotals),
          format: ChartData.buildMuzeTextFormatter(model, [formatterField], fieldFormats),
        });
      }
      // additionally add all other schema fields to the tooltip that should be included
      schema.forEach((field: any) => {
        const modeField = EncodingHelpers.getModeFieldById(model, encoding, field.name);
        if (modeField && !isMeasureNames(modeField)) {
          if (isMeasureValues(modeField)) {
            const measureNamesValue = dataArray[measureNamesIndex];
            const valueModeField =
              measureNamesValue != null
                ? getMeasureValueModeField(model, encoding, measureNamesValue, fieldMap)
                : undefined;
            const toolTipName =
              (valueModeField &&
                FieldFormatHelpers.getBaseFormatDisplayName(
                  FieldFormatHelpers.getFormatMapKeyForModeField(valueModeField),
                  fieldFormats
                )) ||
              String(measureNamesValue ?? modeField.name);
            const fieldFormat = getFieldFormat(model, valueModeField, fieldFormats);
            const formatterField =
              fieldFormat != null && fieldFormat.shortcutType !== ChartTypes.FormatShortcutType.Automatic
                ? valueModeField ?? modeField
                : modeField;
            tooltipRows.push({
              name: toolTipName,
              role: modeField.role,
              ...getValueFromData(dataArray, dataStore, encoding, field.name, isTotals),
              format: ChartData.buildMuzeTextFormatter(model, [formatterField], fieldFormats),
            });
          } else {
            tooltipRows.push({
              name: getFieldDisplayName(model, modeField, fieldFormats, fieldMap),
              role: modeField.role,
              ...getValueFromData(dataArray, dataStore, encoding, field.name, isTotals),
              format: ChartData.buildMuzeTextFormatter(model, [modeField], fieldFormats),
            });
          }
        }
      });
    });
  return tooltipRows;
}

function makeUntargettedTipRows(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  fieldMap: Map<string, CompiledField>,
  targetIds: string[],
  targetValues: string | number[],
  layer: any
) {
  const tooltipRows: Array<TooltipRow> = [];
  // Get the facet information
  const facets: Record<string, string | number> = layer.metaInf().facets;
  // Add the target ids and values
  targetIds.forEach((id, index) => {
    if (id !== REF_ID) {
      facets[id] = targetValues[index];
    }
  });
  // Build a tooltip row for each id and value pair
  Object.entries(facets).forEach(([fieldId, value]) => {
    const modeField = EncodingHelpers.getModeFieldById(model, encoding, fieldId);
    if (modeField) {
      tooltipRows.push({
        name: getFieldDisplayName(model, modeField, fieldFormats, fieldMap),
        role: modeField.role,
        isAllToken: value === TOTALS_TOKEN,
        value: value === TOTALS_TOKEN ? 'All' : value,
        format: ChartData.buildMuzeTextFormatter(model, [modeField], fieldFormats),
      });
    }
  });

  return tooltipRows;
}

/**
 * Builds a table to show all of the color or detail values across the current slice.
 * @param model RenderDataModel
 * @param encoding Encoding
 * @param fieldFormats FieldFormatMap
 * @param targetChannelFieldId string id of the channel being compared
 * @param allDimensions string[] containing the field ids for dimensions on the channels
 * @param dataStore Muze dataStore containing all of the data
 * @param targetIds string [] containing the field ids which are in the current hover target
 * @param targetValues string | number [] containing the values corresponding to the ids in targetIds
 * @param secondaryField ModeField on the response axis
 * @param colorProvider function to retrieve a color for a category value OR a single color string
 * @returns HTML necessary to display the table.
 */
function makeCompareTable(
  model: RenderDataModel,
  encoding: Encoding,
  fieldFormats: ChartTypes.FieldFormatMap,
  targetChannelFieldId: string,
  allDimensions: string[],
  dataStore: any,
  targetIds: string[],
  targetValues: string | number[],
  secondaryField: FlamingoTypes.ModeField,
  colorProvider: any,
  includePercAndTotal: boolean
) {
  const targetChannelModeField = EncodingHelpers.getModeFieldById(model, encoding, targetChannelFieldId);

  if (targetChannelModeField == null) {
    return '';
  }
  // Array of formatters for all of the dimension fields
  const formatters = allDimensions.map((dim: any) => {
    const dimField = EncodingHelpers.getModeFieldById(model, encoding, dim);
    return dimField ? ChartData.buildMuzeTextFormatter(model, [dimField], fieldFormats) : undefined;
  });

  const primaryFormatter = ChartData.getMuzeTextFormatter(model, encoding, targetChannelModeField, fieldFormats);

  // Formatter for the response field
  const responseFormatter = secondaryField
    ? ChartData.buildMuzeTextFormatter(model, [secondaryField], fieldFormats)
    : undefined;

  // Unique key for the targeted data row comprised of the values of all of the dimensions
  const targetValueKey = makeDimensionKey(allDimensions, targetValues, formatters, (fieldId: string) =>
    targetIds.indexOf(fieldId)
  );

  let { data } = dataStore.getData({
    excludeFields: [REF_ID],
  });

  let totalSum = 0;
  let hoverTargetIndex = -1;
  // Build an array of all of the color values and calculate the total sum along the way.
  let colorValues: Array<CompareTableEntry> = data.map((dataArray: any, index: number): CompareTableEntry => {
    const primaryFieldValue = dataArray[dataStore.getFieldIndex(targetChannelFieldId)];
    const primaryLabel = asString(
      primaryFormatter != null
        ? primaryFormatter.call(undefined, { rawValue: primaryFieldValue }, { context: {} })
        : primaryFieldValue
    );

    const responseValue = dataArray[dataStore.getFieldIndex(secondaryField.alias)];
    totalSum += Number.isFinite(responseValue) ? responseValue : 0;
    // Get the color for this category if there is a color axis
    const color =
      typeof colorProvider?.getColor === 'function' ? colorProvider.getColor(primaryFieldValue) : colorProvider;

    const rowKey = makeDimensionKey(allDimensions, dataArray, formatters, (fieldId: string) =>
      dataStore.getFieldIndex(fieldId)
    );
    const isHoverTarget = rowKey === targetValueKey;
    if (isHoverTarget) {
      hoverTargetIndex = index;
    }
    return {
      primary: primaryLabel,
      highlight: isHoverTarget,
      responseValue,
      color,
      hideResponse: false,
      isSummaryRow: false,
    };
  });
  // If there are too many entries, truncate the list with a 'window' around the hover target
  const MAX_COMPARE_TABLE_ENTRIES = 17;
  if (colorValues.length > MAX_COMPARE_TABLE_ENTRIES) {
    let startIndex = Math.max(0, hoverTargetIndex - (MAX_COMPARE_TABLE_ENTRIES - 1) / 2);
    // No reason to replace the 0th element with it's summation. Just add the extra row
    startIndex = startIndex === 1 ? 0 : startIndex;
    let endIndex = startIndex + MAX_COMPARE_TABLE_ENTRIES;
    if (endIndex >= colorValues.length - 1) {
      endIndex = colorValues.length;
      // Adjust the start index since the window moved past the end. The window can only overflow at
      // the start or the end but not both.
      startIndex = Math.max(0, endIndex - MAX_COMPARE_TABLE_ENTRIES);
      startIndex = startIndex === 1 ? 0 : startIndex;
    }

    let displayedValues: Array<CompareTableEntry> = [];

    const addSummarizedEntry = (start: number, end: number, labelPrefix: string, labelSuffix = '') => {
      const range: Array<CompareTableEntry> = colorValues.slice(start, end);
      if (range.length > 0) {
        const rangeTotal = includePercAndTotal
          ? range.reduce(
              (acc: number, value: any) => acc + (Number.isFinite(value.responseValue) ? value.responseValue : 0),
              0
            )
          : range.length;
        displayedValues.push({
          primary: `${labelPrefix}${range.length}${labelSuffix}`,
          highlight: false,
          responseValue: rangeTotal,
          color: undefined,
          hideResponse: !includePercAndTotal,
          isSummaryRow: true,
        });
      }
    };

    if (includePercAndTotal) {
      addSummarizedEntry(0, startIndex, 'Total of previous ');
      displayedValues = displayedValues.concat(colorValues.slice(startIndex, endIndex));
      addSummarizedEntry(endIndex, colorValues.length, 'Total of next ');
    } else {
      addSummarizedEntry(0, startIndex, 'Previous ', ' values');
      displayedValues = displayedValues.concat(colorValues.slice(startIndex, endIndex));
      addSummarizedEntry(endIndex, colorValues.length, 'Next ', ' values');
    }
    colorValues = displayedValues;
  }

  let tableRows = includePercAndTotal
    ? buildCompareTableWithPercentage(colorValues, totalSum, responseFormatter)
    : buildCompareTableWithoutPercentage(colorValues, responseFormatter);

  if (includePercAndTotal) {
    tableRows = addTotalRow(tableRows, totalSum, responseFormatter);
  }
  const gridStyle = includePercAndTotal ? 'grid-with-percent-column' : 'grid-without-percent-column';
  return `<div class="tooltip-compare-grid-container ${gridStyle}">${tableRows}</div>`;
}

export function getClassString(classes: Array<string>) {
  return classes.filter((cls) => cls && cls !== '').join(' ');
}

function buildCompareTableWithPercentage(
  colorValues: CompareTableEntry[],
  totalSum: number,
  responseFormatter: ChartData.MuzeDataFormatFn | undefined
) {
  const percentFormat = {
    ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.Percent),
    type: ChartTypes.FormatType.Percent,
    shortcutType: ChartTypes.FormatShortcutType.Percent,
  };
  const tableRows = colorValues
    .map((row: any, index: number, { length }) => {
      const pctOfTotal = row.responseValue / totalSum;
      const potStr = Number.isFinite(pctOfTotal)
        ? FieldFormatHelpers.numberTypeFormat(pctOfTotal, percentFormat)
        : FieldFormatHelpers.MISSING_VALUE;
      const formattedValue =
        responseFormatter?.call(undefined, { rawValue: row.responseValue }, { context: {} }) ?? row.responseValue;

      const highlight = row.highlight ? 'highlight' : '';
      const isLast = index === length - 1 ? 'last-row' : '';
      const summaryOrLabelValue = row.isSummaryRow ? 'summary-row' : 'label-value';
      const colorDot =
        row.color != null
          ? `<div class="${getClassString(['color-dot', highlight, isLast])}" style="color:${row.color}">●</div>`
          : '';
      return (
        `${colorDot}<div class="${getClassString([summaryOrLabelValue, isLast, highlight])}">${escapeXml(
          row.primary
        )}</div>` +
        `<div class="${getClassString(['number-value', highlight, isLast])}">${formattedValue}</div>` +
        `<div class="${getClassString(['percent-value', highlight, isLast])}">${potStr}</div>`
      );
    })
    .join('');

  return tableRows;
}

function buildCompareTableWithoutPercentage(
  colorValues: CompareTableEntry[],
  responseFormatter: ChartData.MuzeDataFormatFn | undefined
) {
  const tableRows = colorValues
    .map((row: any, index: number, { length }) => {
      const formattedValue = row.hideResponse
        ? ''
        : responseFormatter?.call(undefined, { rawValue: row.responseValue }, { context: {} }) ?? row.responseValue;

      const highlight = row.highlight ? 'highlight' : '';
      const isLast = index === length - 1 ? 'last-row' : '';
      const summaryOrLabelValue = row.isSummaryRow ? 'summary-row' : 'label-value';
      const colorDot =
        row.color != null
          ? `<div class="${getClassString(['color-dot', highlight, isLast])}" style="color:${row.color}">●</div>`
          : '';
      return (
        `${colorDot}<div class="${getClassString([highlight, isLast, summaryOrLabelValue])}">${escapeXml(
          row.primary
        )}</div>` + `<div class="${getClassString(['number-value', highlight, isLast])}">${formattedValue}</div>`
      );
    })
    .join('');

  return tableRows;
}

function addTotalRow(tableRows: string, totalSum: number, responseFormatter: ChartData.MuzeDataFormatFn | undefined) {
  const formattedTotal = responseFormatter?.call(undefined, { rawValue: totalSum }, { context: {} }) ?? totalSum;

  return tableRows.concat(
    '<div class="summary-row label-value total">Total</div>' +
      `<div class="number-value total">${formattedTotal}</div>` +
      '<div class="percent-value total"></div>'
  );
}

function getPrimarySecondaryFields(model: RenderDataModel, encoding: Encoding, layer: any) {
  const yField: string = layer.config().encoding.y.field;
  const xField: string = layer.config().encoding.x.field;

  let primaryId: string | undefined;
  let secondaryId: string | undefined;

  switch (encoding.marks.channel) {
    case 'y': {
      primaryId = yField;
      secondaryId = [...encoding.x, ...encoding.x2, ...encoding.column][0]?.id;
      break;
    }
    case 'x': {
      primaryId = xField;
      secondaryId = [...encoding.row, ...encoding.y, ...encoding.y2][0]?.id;
      break;
    }
    case '*': {
      primaryId = yField;
      secondaryId = [...encoding.column][0]?.id;
      break;
    }
  }

  const primaryField = primaryId ? EncodingHelpers.getModeFieldById(model, encoding, primaryId) : undefined;
  const secondaryField = secondaryId ? EncodingHelpers.getModeFieldById(model, encoding, secondaryId) : undefined;

  return { primaryField, secondaryField };
}

/**
 * @description
 * Determines if the current encoding supports displaying percentages and total in the comparison tooltip.
 * It is only supported for:
 *  - Summable aggregated measures when all aggregations are the same.
 *  - Non-aggregated measures.
 *
 * It is not supported for:
 *  - Aggregate calculations
 *  - Analytic calculations
 *  - Line marks
 */
export function includePercentAndTotal(
  model: RenderDataModel,
  encoding: Encoding,
  mark: string,
  primaryField: FlamingoTypes.ModeField
) {
  if (mark === 'line') {
    // Lines aren't stacked
    return false;
  }
  // Convert an array of EncField to array of ModeField
  const getModeFields = (fields: Readonly<EncField[]>) => {
    return fields.map((encField) => model.makeField(encField));
  };

  const responseFields = primaryField.source !== Attr.Source.Values ? [primaryField] : getModeFields(encoding.value);

  // Returns the aggregation for the field if the field is of a supported type. Otherwise, it returns ''.
  const getEffectiveAggregation = (field: FlamingoTypes.ModeField | undefined) => {
    if (field == null) {
      return '';
    }
    const compiledField = model.compileField(field);
    return compiledField.isValid &&
      ((compiledField.isAggregateStage && compiledField.hasAggFunc) || compiledField.isScalar)
      ? compiledField.aggFunc ?? ExprCall.Name.Agg
      : '';
  };

  let aggregation: string;
  const allowedAggr = [
    ExprCall.Name.Sum,
    ExprCall.Name.Count,
    ExprCall.Name.CountD,
    ExprCall.Name.CountDA,
    ExprCall.Name.Agg,
  ];

  // Return true if all response fields are of the same allowed aggregation type.
  return responseFields.every((field) => {
    const effectiveAggr = getEffectiveAggregation(field);
    if (!allowedAggr.some((agg) => agg === effectiveAggr)) {
      return false;
    }
    if (aggregation == null) {
      aggregation = effectiveAggr;
    }
    return effectiveAggr === aggregation;
  });
}

// This handles the Muze invalid value object by calling toString() on it to
// get the 'null' string out.
function asString(value: any) {
  const strVal: string = value?.toString() ?? FieldFormatHelpers.MISSING_VALUE;
  return strVal === 'NaN' ? FieldFormatHelpers.MISSING_VALUE : strVal;
}

/**
 * Constructs a key for a given row of data which encapsulates the crossings of all of the dimension
 * values.
 * @param dimensions string[] containing the field ids of the dimensions
 * @param dataArray string | number [] containing the data for the particular row
 * @param formatters any[] of formatters used to format the dimension values
 * @param getFieldIndex Function which provides the index into the dataArray for the given dimension field id
 * @returns A string representation of the crossing. Ex: 'West-October-Express'.
 */
function makeDimensionKey(
  dimensions: string[],
  dataArray: string | number[],
  formatters: any[],
  getFieldIndex: (field: string) => number
) {
  return dimensions
    .map((dim: string, index: number) => {
      const value = dataArray[getFieldIndex(dim)] ?? '';
      return asString(
        formatters[index] != null ? formatters[index]?.call(undefined, { rawValue: value }, { context: {} }) : value
      );
    })
    .join('-');
}

function escapeXml(unsafe: string) {
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '&':
        return '&amp;';
      case "'":
        return '&apos;';
      case '"':
        return '&quot;';
      default:
        return c;
    }
  });
}

function isMeasureValues(field: FlamingoTypes.ModeField) {
  return field.formula && field.formula.source === Values.Key;
}

function isMeasureNames(field: FlamingoTypes.ModeField) {
  return field.formula && field.formula.source === Names.Key;
}

function getMeasureValueModeField(
  model: RenderDataModel,
  encoding: Encoding,
  fieldName: string,
  fieldMap: Map<string, CompiledField>
): FlamingoTypes.ModeField | undefined {
  const valueEncField = encoding.value.find((valueField) => {
    const id = valueField.id;
    if (id == null) {
      return false;
    }
    if (!fieldMap.has(id)) {
      fieldMap.set(id, model.compileEncField(valueField));
    }
    const compiled = fieldMap.get(id);
    return compiled != null && compiled.isValid && compiled.displayName === fieldName;
  });
  return valueEncField?.id != null ? EncodingHelpers.getModeFieldById(model, encoding, valueEncField.id) : undefined;
}

function getValueFromData(dataArray: any, dataStore: any, encoding: Encoding, fieldId: string, isTotals: boolean) {
  const value = dataArray[dataStore.getFieldIndex(fieldId)];
  if (value === TOTALS_TOKEN && isTotals) {
    return { value: 'All', isAllToken: true };
  }
  return { value: value, isAllToken: false };
}

function getFieldDisplayName(
  model: RenderDataModel,
  field: FlamingoTypes.ModeField,
  formats: ChartTypes.FieldFormatMap,
  fieldMap: Map<string, CompiledField>
) {
  const fieldKey = FieldFormatHelpers.getFormatMapKeyForModeField(field);
  const fieldFormats = formats[fieldKey];

  // Use the axis or header title if available.
  const titleFormat =
    field.type === Attr.Type.Continuous
      ? ChartData.getAxisTitleFormat(fieldFormats)
      : ChartData.getHeaderTitleFormat(fieldFormats);
  const baseDisplayName = FieldFormatHelpers.getBaseFormatDisplayName(fieldKey, formats);
  let displayName = baseDisplayName
    ? baseDisplayName
    : titleFormat != null && titleFormat?.text
    ? titleFormat.text
    : '';

  if (!displayName) {
    if (!fieldMap.has(field.alias)) {
      fieldMap.set(field.alias, model.compileField(field));
    }
    const compiled = fieldMap.get(field.alias);
    if (compiled != null && compiled.isValid) {
      displayName = compiled.isAttrNumberType ? compiled.displayName : compiled.displayFormula;
    } else {
      displayName = field.name;
    }
  }
  return displayName;
}

function getFieldFormat(
  model: RenderDataModel,
  field: FlamingoTypes.ModeField | undefined,
  fieldFormats: ChartTypes.FieldFormatMap
) {
  // Either return the pane text format or the default attr format
  const paneFormat = field != null ? FieldFormatHelpers.resolvePaneTextFormatForField(fieldFormats, field) : undefined;
  const compiled = field != null ? model.compileField(field) : undefined;
  return compiled ? FieldFormatHelpers.resolveTextFormatForCompiledField(compiled, paneFormat) : paneFormat;
}

/**
 * Removes items from the schema which are defined only in a series other than the one for which the tooltip
 * is being generated.
 * @param encoding
 * @param schema
 * @param contextInfo
 * @returns A new MuzeDataSchema containing only the items associated with the targeted series or all items
 * if there are one or no series.
 */
function removeNonTargetedSeriesSchemaItems(
  encoding: Encoding,
  schema: ChartData.MuzeDataSchema,
  contextInfo: ChartData.MuzeContextInfo
) {
  if (encoding.marks.channel !== '*' && encoding.marks.series.length > 1 && contextInfo.context) {
    const sid = getSID(contextInfo);
    if (sid != null) {
      const seriesFieldsIds = [...encoding.column, ...encoding.row, ...encoding.value].map((field) => field.id ?? '');
      if (encoding.marks.channel === 'x') {
        const yIds = [...encoding.y, ...encoding.y2].map((field) => field.id ?? '');
        seriesFieldsIds.push(...yIds);
      } else {
        const xIds = [...encoding.x, ...encoding.x2].map((field) => field.id ?? '');
        seriesFieldsIds.push(...xIds);
      }
      seriesFieldsIds.push(ChartData.SID_FIELD);
      seriesFieldsIds.push(sid);

      const summary: MarksSummary = EncodingHelpers.getSummary(encoding);

      const addSeriesChannelFields = (channel: MarkChannel, values: readonly Readonly<EncField>[]) => {
        values.forEach((value) => {
          const id = ChartLayers.getFieldOrSummary(encoding.marks.series, channel, summary, value.id);
          if (id != null) {
            seriesFieldsIds.push(id);
          }
        });
      };

      const thisSeries = encoding.marks.series.find((s: any) => s.field === sid);
      if (thisSeries) {
        addSeriesChannelFields('angle', thisSeries.properties.angle.values);
        addSeriesChannelFields('color', thisSeries.properties.color.values);
        addSeriesChannelFields('detail', thisSeries.properties.detail.values);
        addSeriesChannelFields('label', thisSeries.properties.label.values);
        addSeriesChannelFields('size', thisSeries.properties.size.values);
        addSeriesChannelFields('text', thisSeries.properties.text.values);
        addSeriesChannelFields('baseline', thisSeries.properties.baseline.values);
      }

      // Return a new array with the items from other series filtered out
      return schema.filter((schemaItem: any) => {
        return seriesFieldsIds.some((id) => id === schemaItem.name);
      });
    }
  }
  return schema;
}

function getSID(contextInfo: ChartData.MuzeContextInfo) {
  const context = contextInfo.context;
  const targetIds = context.payload.target ? context.payload.target[0] : context.payload.criteria.dimensions[0];
  const targetValues = context.payload.target ? context.payload.target[1] : context.payload.criteria.dimensions[1];
  if (targetIds == null || targetValues == null) {
    return null;
  }
  const sidIndex = targetIds.indexOf(ChartData.SID_FIELD);
  if (sidIndex < 0) {
    return null;
  }
  return targetValues[sidIndex];
}
